import { ARIAS, CLASSES } from '../../utils/constants';

/** Strip trailing slash for path comparison. */
const normalisePath = (path) => (path || '').replace(/\/$/, '');

/**
 * Linked Product Select (Flavour Dropdown)
 *
 * Custom dropdown that navigates to a different product URL on selection.
 * Uses roving tabindex for keyboard accessibility (focus moves with the
 * active option; only one option is in the tab order at a time).
 *
 * All persistent listeners are registered with an AbortController signal
 * so they can be bulk-removed when the DOM element is torn down during a
 * Shopify Theme Editor section reload.
 */
const initLinkedProductSelect = () => {
  const dropdownEls = document.querySelectorAll('[data-linked-product-dropdown]');
  if (!dropdownEls.length) return;

  dropdownEls.forEach((dropdown, dropdownIndex) => {
    if (dropdown.dataset.linkedProductDropdownInitialised === 'true') return;

    const triggerEl = dropdown.querySelector('[data-linked-product-trigger]');
    const listboxEl = dropdown.querySelector('[data-linked-product-listbox]');
    const optionEls = [...dropdown.querySelectorAll('[data-linked-product-option]')];
    const currentLabelEl = dropdown.querySelector('[data-linked-product-current-label]');
    const currentSwatchEl = dropdown.querySelector('[data-linked-product-current-swatch]');

    if (!triggerEl || !listboxEl || !optionEls.length || !currentLabelEl || !currentSwatchEl) return;

    // AbortController lets us bulk-remove every listener via ac.abort()
    const ac = new AbortController();
    const { signal } = ac;

    // Handlers that are added/removed dynamically when the dropdown opens/closes
    const handlers = {};

    // Ensure every option has an id for accessibility and initial aria-selected
    optionEls.forEach((optionEl, optionIndex) => {
      if (!optionEl.id) optionEl.id = `linked-product-option-${dropdownIndex}-${optionIndex}`;
      if (!optionEl.hasAttribute(ARIAS.SELECTED)) {
        optionEl.setAttribute(ARIAS.SELECTED, optionIndex === 0 ? 'true' : 'false');
      }
    });

    let activeIndex = optionEls.findIndex((el) => el.getAttribute(ARIAS.SELECTED) === 'true');
    if (activeIndex < 0) activeIndex = 0;

    /**
     * Update the visual highlight and roving tabindex.
     * @param {number} index - Target option index (wraps around).
     * @param {boolean} moveFocus - Whether to move DOM focus to the option.
     */
    const setActiveIndex = (index, moveFocus = false) => {
      if (!optionEls.length) return;

      if (index < 0) index = optionEls.length - 1;
      if (index >= optionEls.length) index = 0;
      activeIndex = index;

      optionEls.forEach((optionEl, optionIndex) => {
        optionEl.classList.toggle(CLASSES.BG_LIGHT_GREY, optionIndex === activeIndex);
        optionEl.setAttribute('tabindex', optionIndex === activeIndex ? '0' : '-1');
      });

      if (moveFocus) optionEls[activeIndex]?.focus();
    };

    /** Mirror the selected option's label and swatch colour onto the trigger. */
    const syncCurrentValue = (optionEl) => {
      const nextLabel = optionEl.dataset.linkedProductLabel?.trim();
      const nextColor = optionEl.dataset.linkedProductColor?.trim();

      if (nextLabel) currentLabelEl.textContent = nextLabel;

      if (nextColor) {
        currentSwatchEl.style.backgroundColor = nextColor;
      } else {
        currentSwatchEl.style.removeProperty('background-color');
      }
    };

    const closeDropdown = ({ returnFocus = false } = {}) => {
      triggerEl.setAttribute(ARIAS.EXPANDED, 'false');
      listboxEl.classList.add(CLASSES.HIDDEN);
      optionEls.forEach((optionEl) => optionEl.classList.remove(CLASSES.BG_LIGHT_GREY));
      dropdown.removeEventListener('keydown', handlers.onDropdownKeydown);
      document.removeEventListener('pointerdown', handlers.onDocumentPointerDown);
      if (returnFocus) triggerEl.focus();
    };

    const openDropdown = ({ focusSelected = true } = {}) => {
      triggerEl.setAttribute(ARIAS.EXPANDED, 'true');
      listboxEl.classList.remove(CLASSES.HIDDEN);
      // These handlers are added on open and removed on close to keep the
      // global event surface minimal when the dropdown is collapsed.
      dropdown.addEventListener('keydown', handlers.onDropdownKeydown);
      document.addEventListener('pointerdown', handlers.onDocumentPointerDown);
      setActiveIndex(activeIndex, focusSelected);
    };

    const isOpen = () => triggerEl.getAttribute(ARIAS.EXPANDED) === 'true';

    // Guard against double-clicks triggering duplicate navigations
    let navigating = false;

    /** Select an option: update ARIA state, sync the trigger, and navigate. */
    const selectOption = (optionEl) => {
      if (!optionEl || navigating) return;

      optionEls.forEach((item) => item.setAttribute(ARIAS.SELECTED, 'false'));
      optionEl.setAttribute(ARIAS.SELECTED, 'true');
      activeIndex = optionEls.indexOf(optionEl);
      if (activeIndex < 0) activeIndex = 0;

      syncCurrentValue(optionEl);
      closeDropdown();

      const nextUrl = optionEl.dataset.linkedProductUrl;
      if (!nextUrl) return;

      try {
        const targetPath = normalisePath(new URL(nextUrl, window.location.origin).pathname);
        const currentPath = normalisePath(window.location.pathname);
        if (targetPath !== currentPath) {
          navigating = true;
          window.location.href = nextUrl;
        }
      } catch {
        // Malformed URL — navigate directly and let the browser handle it
        navigating = true;
        window.location.href = nextUrl;
      }
    };

    // -- Dynamic handlers (added/removed with open/close) -------------------

    handlers.onDocumentPointerDown = (event) => {
      if (!dropdown.contains(event.target)) closeDropdown();
    };

    handlers.onDropdownKeydown = (event) => {
      if (!isOpen()) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setActiveIndex(activeIndex + 1, true);
          break;
        case 'ArrowUp':
          event.preventDefault();
          setActiveIndex(activeIndex - 1, true);
          break;
        case 'Home':
          event.preventDefault();
          setActiveIndex(0, true);
          break;
        case 'End':
          event.preventDefault();
          setActiveIndex(optionEls.length - 1, true);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          selectOption(optionEls[activeIndex]);
          break;
        case 'Escape':
          event.preventDefault();
          closeDropdown({ returnFocus: true });
          break;
        case 'Tab':
          closeDropdown();
          break;
        default:
          break;
      }
    };

    // -- Persistent listeners (cleaned up via AbortController) --------------

    triggerEl.addEventListener('click', () => {
      if (isOpen()) closeDropdown();
      else openDropdown();
    }, { signal });

    triggerEl.addEventListener('keydown', (event) => {
      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp':
          event.preventDefault();
          openDropdown();
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (isOpen()) closeDropdown();
          else openDropdown();
          break;
        default:
          break;
      }
    }, { signal });

    optionEls.forEach((optionEl) => {
      optionEl.addEventListener('click', () => selectOption(optionEl), { signal });
      optionEl.addEventListener('mousemove', () => {
        const hoveredIndex = optionEls.indexOf(optionEl);
        if (hoveredIndex >= 0) setActiveIndex(hoveredIndex);
      }, { signal });
    });

    // Close when focus leaves the dropdown entirely
    dropdown.addEventListener('focusout', (event) => {
      if (dropdown.contains(event.relatedTarget)) return;
      closeDropdown();
    }, { signal });

    // -- Init ---------------------------------------------------------------

    setActiveIndex(activeIndex);
    syncCurrentValue(optionEls[activeIndex]);
    dropdown.dataset.linkedProductDropdownInitialised = 'true';

    // Tear down all listeners if the dropdown is removed from the DOM (section reload).
    // Observes document.body with subtree so removal of any ancestor is caught.
    const teardownObserver = new MutationObserver(() => {
      if (!dropdown.isConnected) {
        ac.abort();
        teardownObserver.disconnect();
      }
    });
    teardownObserver.observe(document.body, { childList: true, subtree: true });
  });
};

export default initLinkedProductSelect;
